import { useAuth0 } from '@auth0/auth0-react';
import { FormEvent, useEffect, useState } from 'react';
import { useQuery } from '@apollo/client';
import { Icon, Stack, TextField } from '@fluentui/react';
import { GET_TAX_CODES, ITaxCode } from '../../utils/TaxCode';
import {
  DEFAULT_ERROR_MESSAGE,
  default_page_size,
  stackTokens15,
  textFieldStyles300,
} from '../../utils';
import useDebounce from '../../components/hooks/useDebounce';
import { GET_COST_CENTERS } from '../../utils/CostCenter';
import { toastError } from '../../utils/toast';
import CostCentersOverview from '../cost-center/components/CostCentersOverview';
import PageHeader from '../../components/parts/PageHeader';
import TaxCodesOverview from './TaxCodesOverview';

interface IPagedTaxCodes {
  taxCodes: ITaxCode[];
  skip: number;
  take: number;
  filter: string;
  initialLoad: boolean;
  showLoadMore: boolean;
  lazyLoading: boolean;
  isFiltered: boolean;
  isSortedAsc: boolean;
  sortedField: string;
}

const TaxCodes = () => {
  const { isAuthenticated } = useAuth0();
  const [pagedState, setPagedState] = useState<IPagedTaxCodes>({
    taxCodes: [],
    skip: 0,
    take: default_page_size,
    filter: '',
    showLoadMore: true,
    lazyLoading: true,
    isFiltered: false,
    initialLoad: true,
    isSortedAsc: true,
    sortedField: 'label',
  });

  const [filter, setFilter] = useState('');
  const debouncedValue = useDebounce(filter, 500);

  useEffect(() => {
    setPagedState(prevState => ({ ...prevState, taxCodes: [], skip: 0 }));
  }, [filter]);

  const getOrderBy = () => {
    const orderByObject: any = {}; // deliberately kept any as type to index by string
    orderByObject[pagedState.sortedField] = pagedState.isSortedAsc
      ? 'asc'
      : 'desc';
    return orderByObject;
  };

  const getFilters = (filterString: string) => {
    const filterArray = filterString.split(' ');

    const filterObject: any = {
      AND: [],
    };
    for (let i = 0; i < filterArray.length; i++) {
      const filterValue: any = {
        OR: [{ label: { contains: filterArray[i] } }],
      };

      filterObject.AND.push(filterValue);
    }

    return filterObject;
  };

  const {
    data: dataTaxCodes,
    loading,
    error,
    fetchMore,
    refetch,
  } = useQuery(GET_TAX_CODES, {
    // Geen client.cache.reset() meer bij zoeken/sorteren: elke filter + sortering heeft een eigen
    // cache-ingang (skipTakePagination in App.tsx). Eerst tonen wat in de cache zit en altijd
    // verversen bij de server; daarna cache-first, zodat "meer laden" geen extra verversing uitlokt.
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: 'cache-first',
    // SnotifyOnNetworkStatusChange: true,
    variables: {
      filter: getFilters(debouncedValue),
      take: pagedState.take,
      skip: 0,
      orderBy: getOrderBy(),
    },
    onError: error => {
      toastError(error.message || DEFAULT_ERROR_MESSAGE);
    },
    // fetchPolicy: 'no-cache',
  });

  const onSearchValueChange = (
    event: FormEvent<HTMLInputElement | HTMLTextAreaElement>,
    newValue?: string | undefined,
  ) => {
    const filterString = newValue ? newValue.toLowerCase() : '';
    setFilter(filterString);
  };

  const clearTaxCodes = () => {
    setPagedState(prevState => ({
      ...prevState,
      taxCodes: [],
      skip: 0,
    }));
  };

  const setSorting = (isSortedAsc: boolean, sortedField: string) => {
    clearTaxCodes();
    setPagedState(prevState => ({
      ...prevState,
      isSortedAsc,
      sortedField,
    }));
  };

  const fetchMoreTaxCodes = () => {
    // verder vanaf wat er getoond wordt (zoals bij leads/klanten)
    const currentCount = dataTaxCodes?.findManyTaxCodes?.length || 0;
    // Load more callback

    fetchMore({
      variables: {
        filter: getFilters(debouncedValue),
        take: pagedState.take,
        skip: currentCount,
        orderBy: getOrderBy(),
      },
    });

    setPagedState(prevState => ({
      ...prevState,
      skip: currentCount,
    }));
  };

  if (!isAuthenticated) return <p>Verboden</p>;
  if (error) return <p>Oeps, er ging iets mis...</p>;

  return (
    <Stack tokens={stackTokens15}>
      <div style={{ padding: '25px' }}>

        <PageHeader
          title='Belastingcodes'
          iconName='Coupon'
          loading={loading}
        />

        <TextField
          label='Zoeken...'
          value={filter}
          styles={textFieldStyles300}
          onChange={onSearchValueChange}
        />
      </div>

      <TaxCodesOverview
        taxCodes={dataTaxCodes ? dataTaxCodes.findManyTaxCodes : []}
        loading={loading}
        setSorting={setSorting}
        isSortedAsc={pagedState.isSortedAsc}
        sortedField={pagedState.sortedField}
        showLoadMore={pagedState.showLoadMore}
        loadMore
        loadMoreCallback={fetchMoreTaxCodes}
        lazyLoading={pagedState.lazyLoading}
        refetch={refetch}
      />
    </Stack>
  );
};

export default TaxCodes;
